"use client";

import React, { useState } from "react";
import Image from "next/image";
import { Link as LinkIcon } from "lucide-react";
import type { FormattedPost } from "@/types";

interface PreviewData {
  title: string;
  description: string;
  image: string;
  domain: string;
  type?: "image" | "video";
}

const getFaviconUrl = (domain: string) =>
  `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;

/** Extract hostname from a URL string, cleaned (no www.), or return fallback */
function getDomainFromUrl(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./i, "");
  } catch {
    return "Post";
  }
}

/** Remove backend tag pattern #[tag_name](tag:0xtag_id) from content */
function stripTagsFromContent(content: string): string {
  return content
    .replace(/#\[[^\]]*\]\(tag:[^)]*\)/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Find first URL in text (for domain fallback when post.links is empty) */
function getFirstUrlFromContent(content: string): string | null {
  const match = content.match(/\b(https?:\/\/[^\s]+)/i);
  return match ? match[1].replace(/[.,;:!?)]+$/, "") : null;
}

/** Build link-preview data from post: title/description from content split by newline, image from media, domain from link. */
function buildPreviewFromPost(post: FormattedPost): PreviewData {
  const rawContent = post.content?.trim() || "";
  const content = stripTagsFromContent(rawContent);
  const parts = content.split(/\n/);
  const title = (parts[0] ?? "").trim();
  const description = parts.slice(1).join("\n").trim();

  const media = Array.isArray(post.media) ? post.media : [];
  const mediaType = (t: string) => (t || "").toLowerCase();
  const firstImage = media.find((m) => mediaType(m.type) === "image");
  const firstVideo = media.find((m) => mediaType(m.type) === "video");
  const firstMedia = media[0];
  const getMediaUrl = (
    m: { url?: string; thumbnail_url?: string } | undefined
  ) => m?.url || (m as { thumbnail_url?: string })?.thumbnail_url || "";
  const imageUrl =
    getMediaUrl(firstImage) ||
    getMediaUrl(firstVideo) ||
    getMediaUrl(firstMedia) ||
    "";
  const type: "image" | "video" | undefined = firstVideo
    ? "video"
    : firstImage
      ? "image"
      : firstMedia && mediaType(firstMedia.type) === "video"
        ? "video"
        : firstMedia
          ? "image"
          : undefined;

  const links = post.links;
  const firstLink =
    Array.isArray(links) && links.length > 0
      ? typeof links[0] === "string"
        ? links[0]
        : (links[0] as { url?: string })?.url
      : getFirstUrlFromContent(rawContent);
  const domain = firstLink ? getDomainFromUrl(firstLink) : "Post";

  return {
    title: title || "Post",
    description,
    image: imageUrl,
    domain,
    type,
  };
}

interface AutomatedPostLinkPreviewProps {
  post: FormattedPost;
}

const AutomatedPostLinkPreview: React.FC<AutomatedPostLinkPreviewProps> = ({
  post,
}) => {
  const previewData = buildPreviewFromPost(post);
  const [faviconError, setFaviconError] = useState(false);

  return (
    <div className="mt-2 space-y-2" key={post.id}>
      <div
        className="border border-border rounded-lg overflow-hidden bg-card hover:bg-secondary/10"
        role="article"
        aria-label={`Preview: ${previewData.title}`}
      >
        <div className="flex">
          <div className="flex-grow p-3">
            <div className="text-xs text-muted-foreground mb-1 flex items-center">
              {!faviconError && previewData.domain !== "Post" ? (
                <Image
                  src={getFaviconUrl(previewData.domain)}
                  alt={`Favicon of ${previewData.domain}`}
                  width={16}
                  height={16}
                  className="mr-2"
                  onError={() => setFaviconError(true)}
                />
              ) : (
                <LinkIcon size={16} className="mr-2 text-muted-foreground" />
              )}
              {previewData.domain}
            </div>
            <h3 className="font-bold text-sm line-clamp-2 mb-1">
              {previewData.title}
            </h3>
            {previewData.description ? (
              <p className="text-xs text-muted-foreground line-clamp-2">
                {previewData.description}
              </p>
            ) : null}
          </div>
          {previewData.image ? (
            <div className="w-24 h-24 relative flex-shrink-0">
              {previewData.type === "video" ? (
                <video
                  src={previewData.image}
                  autoPlay
                  muted
                  loop
                  controls
                  className="w-full h-full object-cover"
                />
              ) : (
                <img
                  src={previewData.image}
                  alt={previewData.title || "Preview image"}
                  width={96}
                  height={96}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AutomatedPostLinkPreview;
